/**
 * Helpers for DRF page-number pagination.
 * Keeps admin screens from requesting huge single payloads (page_size 500–1000).
 */

export function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

// Loading failures and malformed successes must never become a valid empty list.
export function requireListResponse(data) {
  if (Array.isArray(data)) return { results: data, count: data.length, next: null, previous: null };
  if (data && Array.isArray(data.results) && Number.isInteger(data.count) && data.count >= 0
      && data.results.length <= data.count && !(data.count > 0 && data.results.length === 0)) return data;
  throw new Error('The server returned an incomplete list. Please try again.');
}

/**
 * Walk DRF `next` pages via a fetch function that accepts { page, page_size, ... }.
 * Stops when there is no `next`. Throws if maxPages is exceeded so callers
 * never silently operate on a truncated roster / dataset.
 */
export async function fetchAllPages(fetchPage, params = {}, options = {}) {
  const pageSize = options.pageSize || params.page_size || 50;
  const maxPages = options.maxPages || 40;
  const results = [];
  let page = 1;

  while (page <= maxPages) {
    const data = await fetchPage({ ...params, page, page_size: pageSize });
    if (Array.isArray(data)) {
      return data;
    }
    results.push(...(data?.results || []));
    if (!data?.next) {
      return results;
    }
    page += 1;
  }

  throw new Error(
    `Too many pages to load (limit ${maxPages} × ${pageSize}). Narrow the date range or filters and try again.`
  );
}

/**
 * Resolve a DRF absolute `next` URL into an apiClient-relative path + search.
 */
export function toApiRelativeUrl(nextPageUrl) {
  try {
    const parsed = new URL(nextPageUrl);
    return parsed.pathname.replace(/^\/api/, '') + parsed.search;
  } catch (_) {
    return String(nextPageUrl).replace(/^https?:\/\/[^/]+\/api/, '');
  }
}

/**
 * Build a DRF-like page envelope (used by demo API and tests).
 */
export function buildPageEnvelope(items, params = {}, options = {}) {
  const list = Array.isArray(items) ? items : [];
  const page = Math.max(1, Number(params.page) || 1);
  const requested = Number(params.page_size);
  const defaultSize = options.defaultPageSize || 20;
  const maxSize = options.maxPageSize || 100;
  const pageSize = Math.min(maxSize, Math.max(1, Number.isFinite(requested) && requested > 0 ? requested : defaultSize));
  const start = (page - 1) * pageSize;
  const results = list.slice(start, start + pageSize);
  const hasNext = start + pageSize < list.length;
  const hasPrev = page > 1;
  const basePath = options.basePath || '';
  const mk = (p) => {
    if (!basePath) return hasNext || hasPrev ? `?page=${p}&page_size=${pageSize}` : null;
    return `${basePath}?page=${p}&page_size=${pageSize}`;
  };
  return {
    count: list.length,
    next: hasNext ? mk(page + 1) : null,
    previous: hasPrev ? mk(page - 1) : null,
    results,
  };
}
